<script lang="ts" module>
    export const route = "/admin/mailbox";
</script>

<script lang="ts">
    import * as Select from "../lib/components/ui/select/index.js";
    import MainSidebar from "../lib/components/sidebar/sidebar.svelte";
    import * as Sidebar from "../lib/components/ui/sidebar/index.js";
    import { cn, getClient } from "../lib/utils.js";
    import { useLocalStorage } from "../lib/local-storage.svelte.js";
    import { Input } from "../lib/components/ui/input/index.js";
    import Date from "../lib/components/common/date.svelte";
    import EllipsisVertical from "@lucide/svelte/icons/ellipsis-vertical";
    import ArrowRightToLine from "@lucide/svelte/icons/arrow-right-to-line";
    import ArrowLeftToLine from "@lucide/svelte/icons/arrow-left-to-line";
    import * as DropdownMenu from "../lib/components/ui/dropdown-menu/index.js";
    import { Button } from "../lib/components/ui/button/index.js";
    import MailboxThread from "../lib/components/mailbox-thread.svelte";
    import { setBreadcrumbs } from "../lib/components/breadcrumbs.svelte";
    import AssistantIframe from "../lib/components/assistant-iframe.svelte";
    import type { Res } from "@repo/backend/client";
    import { z } from "@repo/utils";
    import { m } from "../lib/messages.svelte.js";
    import * as Pagination from "../lib/components/ui/pagination/index.js";
    import { curtain } from "../lib/components/common/curtain.svelte.js";
    import { untrack } from "svelte";
    import type { InboxId } from "@repo/backend/types";
    import Truncated from "../lib/components/common/truncated.svelte";

    let externalThreadId = $state<string | null>(null);
    let inboxes = $state<Res["Mailbox view"]["inboxes"]>();
    let threads = $state<Res["Mailbox view"]["threads"]>([]);
    let selectedInboxId = $state<InboxId>();
    let searchInput = $state("");
    let searchQuery = $state("");
    let searchTimeout = $state<ReturnType<typeof setTimeout>>();
    let page = $state(1);
    let loading = $state(true);
    let ac = $state<AbortController>();
    let pageSize = 20;

    const inboxId = $derived(selectedInboxId ?? inboxes?.[0]?.id);
    const inbox = $derived(inboxes?.find((x) => x.id === inboxId));
    const ls = useLocalStorage("assistant", { wide: z.boolean() });

    async function fetchMailbox(inboxId: InboxId | undefined, searchQuery: string, page: number) {
        loading = true;
        untrack(() => {
            if (ac) {
                ac.abort();
            }
        });
        const ac2 = new AbortController();
        ac = ac2;
        const data = await getClient({ fetch, fetchOptions: { signal: ac2.signal } })["Mailbox view"]({
            query: {
                ...(searchQuery ? { q: searchQuery } : {}),
                ...(inboxId ? { inboxId } : {}),
                page: String(page),
            },
        });
        inboxes = data.inboxes;
        threads = data.threads;
        loading = false;
    }

    $effect(() => {
        fetchMailbox(inboxId, searchQuery, page);
    });

    $effect.pre(() => {
        setBreadcrumbs([{ title: m["mailbox"]() }, { title: inbox?.email ?? "...", snippet: inboxSelect }]);
    });
</script>

<MainSidebar>
    {#if inbox}
        <div class="flex grow overflow-x-hidden">
            <Sidebar.Root collapsible="none" class="hidden border-r md:flex" style="--sidebar-width: 24rem">
                <div class="flex h-full flex-col">
                    <div class="border-b p-2">
                        <Input
                            type="search"
                            placeholder={m["Search by subject…"]()}
                            bind:value={
                                () => searchInput,
                                (v) => {
                                    searchInput = v;
                                    clearTimeout(searchTimeout);
                                    searchTimeout = setTimeout(() => {
                                        searchQuery = v.trim();
                                        page = 1;
                                    }, 400);
                                }
                            }
                        />
                    </div>
                    <Sidebar.Content {@attach curtain(() => loading)} class="grow overflow-auto">
                        <Sidebar.Group class="p-0">
                            <Sidebar.GroupContent>
                                {#each threads as t (t.id)}
                                    {@const msg = t.messages[0]}
                                    <!-- svelte-ignore a11y_click_events_have_key_events -->
                                    <!-- svelte-ignore a11y_no_static_element_interactions -->
                                    <div
                                        onclick={() => {
                                            externalThreadId = t.externalId;
                                        }}
                                        class={cn(
                                            {
                                                "bg-gray-200 dark:bg-gray-700": t.externalId === externalThreadId,
                                            },
                                            "group hover:bg-sidebar-accent hover:text-sidebar-accent-foreground relative flex flex-col items-start gap-2 border-b p-4 text-sm leading-tight last:border-b-0",
                                        )}
                                    >
                                        <div class="flex w-full items-center gap-2">
                                            <Truncated value={msg?.senderName || "-"} />
                                            <span class="ml-auto text-xs"><Date value={msg?.receivedAt || "-"} /></span>
                                        </div>
                                        <div class="w-full font-medium wrap-break-word">
                                            {msg?.subject || "No subject"}
                                        </div>
                                        <span class="w-full line-clamp-2 text-xs whitespace-break-spaces">
                                            {msg?.snippet || "No message body"}
                                        </span>
                                        <DropdownMenu.Root>
                                            <DropdownMenu.Trigger>
                                                <div
                                                    class="hover:bg-success absolute top-0 right-0 flex h-full items-center hover:cursor-pointer"
                                                >
                                                    <EllipsisVertical class="not-group-hover:invisible" />
                                                </div>
                                            </DropdownMenu.Trigger>
                                            <DropdownMenu.Content portalProps={{ disabled: true }}>
                                                <DropdownMenu.Group>
                                                    <DropdownMenu.Item
                                                        onclick={() => {
                                                            void getClient()["Reprocess thread"]({
                                                                jsonBody: { threadId: t.id },
                                                            });
                                                        }}
                                                    >
                                                        Reprocess
                                                    </DropdownMenu.Item>
                                                </DropdownMenu.Group>
                                            </DropdownMenu.Content>
                                        </DropdownMenu.Root>
                                    </div>
                                {/each}
                            </Sidebar.GroupContent>
                        </Sidebar.Group>
                    </Sidebar.Content>
                    <div class="flex items-center justify-between border-t px-3 py-2">
                        <Pagination.Root
                            count={threads.length < pageSize ? page * pageSize : Number.POSITIVE_INFINITY}
                            perPage={pageSize}
                            {page}
                            onPageChange={(p) => {
                                page = p;
                            }}
                        >
                            {#snippet children({ currentPage })}
                                <Pagination.Content>
                                    <Pagination.Item>
                                        <Pagination.PrevButton />
                                    </Pagination.Item>
                                    <Pagination.Item>
                                        {currentPage}
                                    </Pagination.Item>
                                    <Pagination.Item>
                                        <Pagination.NextButton />
                                    </Pagination.Item>
                                </Pagination.Content>
                            {/snippet}
                        </Pagination.Root>
                    </div>
                </div>
            </Sidebar.Root>

            <div class="flex h-full grow overflow-x-hidden">
                <div class="grow overflow-x-hidden border-r">
                    {#if externalThreadId}
                        <MailboxThread {externalThreadId} {inboxId} />
                    {/if}
                </div>
                <div class="relative h-full transition-[flex]" style:flex={ls.wide ? "0 0 400px" : "0 0 270px"}>
                    <Button
                        class="text-muted-foreground absolute mt-1 -ml-1 -translate-x-full"
                        variant="ghost"
                        size="icon"
                        onclick={() => (ls.wide = !ls.wide)}
                    >
                        {#if ls.wide}
                            <ArrowRightToLine />
                        {:else}
                            <ArrowLeftToLine />
                        {/if}
                    </Button>
                    <AssistantIframe data={externalThreadId ? { threadId: externalThreadId } : undefined} />
                </div>
            </div>
        </div>
    {:else if inboxes}
        There is no registered inbox for the organization
    {/if}
</MainSidebar>

{#snippet inboxSelect()}
    <Select.Root
        type="single"
        value={inboxId}
        onValueChange={(v) => {
            selectedInboxId = v as InboxId;
            page = 1;
        }}
    >
        <Select.Trigger class="w-full">
            <span class="overflow-hidden overflow-ellipsis">{inbox?.email}</span>
        </Select.Trigger>
        <Select.Content>
            <Select.Group>
                {#each inboxes ?? [] as x (x.id)}
                    <Select.Item value={x.id}>{x.email} ({x.idempotencyKey})</Select.Item>
                {/each}
            </Select.Group>
        </Select.Content>
    </Select.Root>
{/snippet}
